import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { SettlementSummaryRow } from '../../shared/settlement-summary/settlement-summary.component';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, finalize, map, switchMap } from 'rxjs';
import { reportInvalidFormFields } from '@app/utilities';
import { TranslateService } from '@ngx-translate/core';
import {
  ApPayment,
  ApPaymentAllocation,
  ApPaymentAllocationInsert,
  ApPaymentInsert,
  ApPaymentSettlementRate,
  ApPaymentSettlementRateInsert,
  ApPaymentStatus,
  S3Object,
} from '../../../models/ap-payment/ap-payment';
import { CashRequestStatus } from '../../../models/cash-request/cash-request';
import { CashRequestProxyService } from '../../../proxies/cash-request.proxy.service';
import { ExchangeRateProxyService } from '../../../proxies/exchange-rate.proxy.service';
import {
  ApPaymentAllocationFormGroup,
  ApPaymentFormGroup,
  ApPaymentSettlementRateFormGroup,
} from '../../../models/ap-payment/ap-payment-form';
import {
  ApDocumentType,
  Currency,
  PayableDocument,
  distributePayable,
  isPayableBaseGroup,
  payableRowDefault,
} from '../../../models/ap-document/ap-document';
import { ApPaymentRequest, ApPaymentRequestStatus } from '../../../models/ap-payment-request/ap-payment-request';
import { ApPaymentProxyService } from '../../../proxies/ap-payment.proxy.service';
import { ApDocumentProxyService } from '../../../proxies/ap-document.proxy.service';
import { ApAdvanceProxyService } from '../../../proxies/ap-advance.proxy.service';
import { ApPaymentRequestProxyService } from '../../../proxies/ap-payment-request.proxy.service';
import { BankAccountProxyService } from '../../../proxies/bank-account.proxy.service';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { PoReceiptProxyService } from '../../../proxies/po-receipt.proxy.service';
import { PoReceiptCoverage, PoReceiptCoverageRequest } from '../../../models/po-receipt/po-receipt';
import { watchReceivedCoverage } from '../../../utilities/received-coverage.util';
import { Business, BusinessEntityType } from '../../../models/business';
import { ProviderBankAccount, ProviderBankType } from '../../../models/provider/provider-details';
import { isConvertible } from '../../../models/currency-pairs';
import { currencyLabelFromApi } from '../../../utilities/currency.util';
import { exchangeRateBoundsFor, isPlausibleExchangeRate } from '../../../utilities/exchange-rate-bounds';
import { RATE_DECIMALS, followsTableRate, tableRateDate, toFunctionalMultiplier } from '../../../utilities/settlement-rate.util';
import { BankAccount, BankAccountStatus } from '../../../models/bank-account/bank-account';
import { EnumService } from '../../../services/enum.service';
import { SiteCompanyService } from '../../../services/site-company.service';
import { BreadcrumbService, ModalService, SiteService } from '@card-software/library';
import { ApDocumentQuickViewModalComponent } from '../../shared/modals/ap-document-quick-view-modal/ap-document-quick-view-modal.component';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { SupportRoles } from '../../../models/roles/roles-group';

/** Which of the speed-dial paths started this payment. */
type PaymentSource = 'document' | 'request';

interface RequestOption {
  label: string;
  value: string;
}

interface SelectOption {
  label: string;
  value: string | number;
}

@Component({
  selector: 'app-ap-payment-details',
  templateUrl: './ap-payment-details.component.html',
  styleUrl: './ap-payment-details.component.scss',
})
export class ApPaymentDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(ApPaymentProxyService);
  private readonly exchangeRateService = inject(ExchangeRateProxyService);
  private readonly documentService = inject(ApDocumentProxyService);
  private readonly advanceService = inject(ApAdvanceProxyService);
  private readonly requestService = inject(ApPaymentRequestProxyService);
  private readonly bankAccountService = inject(BankAccountProxyService);
  private readonly businessService = inject(BusinessProxyService);
  private readonly poReceiptService = inject(PoReceiptProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly auth = inject(UserAuthenticationService);
  private readonly enumService = inject(EnumService);
  private readonly cashRequestService = inject(CashRequestProxyService);
  private readonly siteCompany = inject(SiteCompanyService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly ApPaymentStatus = ApPaymentStatus;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  status = signal<number>(ApPaymentStatus.Draft);
  headerTitle = signal<string>(this.t('ApPayment_New', 'New payment'));
  paymentNumber = signal<string>('');

  // ---- Accounting / GL impact (#5024 #5031) ----
  /** The journal entry this payment booked when it posted. Null on a draft. */
  readonly journalEntryReference = signal<string | null>(null);
  readonly journalEntryDocumentNumber = signal<string | null>(null);
  readonly showAccounting = computed(() => !!this.journalEntryReference());
  /** The component takes a list; a payment books exactly one entry. */
  readonly journalEntryReferences = computed(() => {
    const reference = this.journalEntryReference();
    return reference ? [reference] : [];
  });

  attachments = signal<S3Object[]>([]);
  autoCreated = signal(false);
  requestId = signal<string | null>(null);

  // Which speed-dial path opened this page (only meaningful for a brand-new payment).
  source = signal<PaymentSource>('document');
  // Mirrors the currency form control so it can drive the bank/document computeds reactively.
  currency = signal<Currency>(Currency.HNL);

  /** Approved cash requests this payment's lines can be tagged against (#4796). Empty is fine — the tag is
   *  optional, and a company with no approved plan simply has nothing to offer. */
  cashRequestOptions = signal<SelectOption[]>([]);
  // Full active bank-account set; the visible options are filtered by the chosen currency.
  private readonly allBankAccounts = signal<BankAccount[]>([]);

  // Payment Request path: pick an Approved request, which opens (or spawns) its 1:1 Draft payment.
  approvedRequests = signal<ApPaymentRequest[]>([]);
  readonly selectedRequestControl = new FormControl<string | null>(null);

  // amount = total CASH allocated (settlement − advance); totalAdvance = advances drawn down (#4349).
  amount = signal(0);
  totalAdvance = signal(0);
  /** Whether the bank account currently carries `Validators.required` — see syncBankAccountRequirement. */
  private bankAccountRequired = true;
  /** Nothing to pay: the advances cover the whole settlement, so this payment moves no money (#4349). */
  readonly fullyCoveredByAdvance = computed(() => this.amount() <= 0 && this.totalAdvance() > 0);
  /** Last value the quote rows were synced against — see recomputeTotals. */
  private wasFullyCoveredByAdvance = false;

  /**
   * The advance being drawn down, shown above the cash so the summary is not a column of zeroes.
   *
   * <p>A payment the advance covers in full settles 49.99 and pays 0.00; a summary showing only the second
   * figure reads as an empty payment rather than a settled one.</p>
   */
  readonly summaryRows = computed<SettlementSummaryRow[]>(() =>
    this.totalAdvance() > 0
      ? [{ label: this.t('ApPayment_Summary_AdvanceApplied', 'Settled by vendor advance'), value: this.totalAdvance(), tone: 'muted' }]
      : [],
  );
  selectedCount = signal(0);
  totalInvoices = signal(0);
  // Bank charges (outgoing → added on top of the allocated amount).
  bankingFeeValue = signal(0);
  securityFeeValue = signal(0);
  readonly totalCashOut = computed(() => Math.round((this.amount() + this.bankingFeeValue() + this.securityFeeValue()) * 100) / 100);
  /** Base doc id → its payable aggregate (notes + withholding + net), for the adjustment flag + resumen (#4516). */
  private readonly payableByDoc = new Map<string, PayableDocument>();
  /** PO reference → advance still available against it (#4349), from posted Anticipos. */
  private readonly advanceAvailByPo = new Map<string, number>();
  /** Saved drawdowns being restored onto a reloaded draft (row doc-ref → the stored advanceAppliedAmount).
   * Consumed and cleared by `applyAdvanceCaps`, which restores these (clamped to what is still available)
   * instead of auto-filling them — auto-fill is a default being offered, this is a commitment being
   * reopened. Null on every path except the draft reload. */
  private restoredAdvanceByDoc: Map<string, number> | null = null;
  /** True from the moment a reloaded draft asks for its payable list until its rows are rebuilt. While set,
   * `syncSettlementRates` must leave the rate rows alone: with zero allocation rows on screen it would read
   * the payment as converting nothing and prune the very rates the draft just restored — and the bank's
   * quote, never being prefilled, would come back as an empty field with the saved figure gone. The bank
   * list racing the payable fetch is exactly when that happened. */
  private restoringAllocations = false;
  /** Row-expansion state, keyed by document id — shows the resumen inline. */
  expandedRows: Record<string, boolean> = {};

  private readonly bankAccountMap = new Map<string, BankAccount>();
  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};

  /** Rate precision for the quote inputs — the API's, so a bank quote is kept whole (#5359). */
  protected readonly rateDecimals = RATE_DECIMALS;

  readonly isDraft = computed(() => this.status() === ApPaymentStatus.Draft);

  /** Per document: would paying its allocation take its PO past what was received (#5662)? Advice only —
   * checked while the payment is a draft; the post applies the rule itself. */
  readonly receivedCoverage = signal<Map<string, PoReceiptCoverage>>(new Map());
  /** The SELECTED allocations' check results, for the banner. Re-reads when the totals move, i.e. on (de)selection. */
  readonly selectedCoverage = computed(() => {
    this.amount();
    const coverage = this.receivedCoverage();
    return this.allocations.controls
      .filter((row) => row.controls.selected.value)
      .map((row) => coverage.get(row.controls.documentReference.value))
      .filter((c): c is PoReceiptCoverage => c !== undefined);
  });
  readonly isPosted = computed(() => this.status() === ApPaymentStatus.Posted);

  /**
   * Returning a POSTED payment to draft is reserved for the FinanceTransactionReversal support role — not an
   * ordinary editor's call, and not an AP admin's.
   *
   * <p>Deleting a draft deliberately carries no extra role — it is the same authority as editing one, and a
   * draft has posted nothing. This is the opposite case: the entry is on the ledger, the invoices read Paid
   * and the bank balance has moved, so undoing it is a different kind of act from editing one.</p>
   *
   * <p>Hiding the button is a courtesy, not the enforcement. A role only the client checks is a suggestion
   * — see PaymentCurrencyGate for what that costs — so the API has to carry the same rule.</p>
   */
  readonly canUnpost = this.auth.roles.includes(SupportRoles.FinanceTransactionReversal);
  readonly isPersisted = computed(() => !this.isNew() && !!this.currentId());
  /** This payment was spawned from an approved request: its provider and invoice allocation are fixed by the request. */
  readonly fromRequest = computed(() => !!this.requestId());
  readonly isRequestMode = computed(() => this.source() === 'request');
  /** Show the request picker only while choosing the request for a new payment; once a payment is loaded, show the form. */
  readonly showRequestPicker = computed(() => this.isNew() && this.isRequestMode());
  readonly showPaymentForm = computed(() => !this.showRequestPicker());
  /**
   * Every active account, in any currency (#5135).
   *
   * <p>It used to be narrowed to the payment's currency, which had the relationship backwards: the money
   * leaves a real account and that account's currency IS what the payment is in. Choosing the account now
   * sets the currency (see <c>onBankAccountChange</c>), which satisfies the funding gate by construction
   * rather than by filtering — and lets a Lempira account pay a dollar invoice, since what converts is the
   * discharge rather than the money.</p>
   */
  readonly bankAccountOptions = computed<SelectOption[]>(() =>
    this.allBankAccounts()
      .filter((acc) => !!acc.id)
      .map((acc) => ({ label: this.bankLabel(acc), value: acc.id! })),
  );

  /** The provider's own accounts, refreshed when a provider is picked (#5143). */
  readonly providerBanks = signal<ProviderBankAccount[]>([]);

  /** The payment answers a request that named where to pay. A locked field with no explanation reads as
   *  broken rather than as a rule, so the screen says whose decision it was. */
  readonly destinationLockedToRequest = signal(false);

  /**
   * Destinations narrowed to the payment currency.
   *
   * <p>The API refuses a destination in another currency until real bank conversion exists (#5145), so
   * offering one here would be offering a choice that cannot be saved. Same reasoning as
   * `bankAccountOptions` above, applied to the other end of the transfer.</p>
   *
   * <p>Intermediary accounts are left out for a different reason: a correspondent bank is a leg of the
   * route, not where the money comes to rest. The API refuses one outright, so listing it would offer a
   * destination that is not one.</p>
   */
  readonly providerBankOptions = computed<SelectOption[]>(() =>
    this.providerBanks()
      .filter(
        (bank) => bank.id && isConvertible(this.currency(), bank.currency as Currency) && bank.bankType !== ProviderBankType.Intermediary,
      )
      .map((bank) => ({ label: this.providerBankLabel(bank), value: bank.id! })),
  );

  readonly form: FormGroup<ApPaymentFormGroup> = this.formBuilder.group<ApPaymentFormGroup>({
    provider: new FormControl<Business | null>(null, [Validators.required]),
    paymentDate: new FormControl<Date | null>(new Date(), [Validators.required]),
    bankAccount: new FormControl<string | null>(null, [Validators.required]),
    // Deliberately NOT required: 43 of the 53 providers on file have no bank accounts, and a payment that
    // cannot say where the money went is still a payment (#5143).
    selectedProviderBankId: new FormControl<string | null>(null),
    bankTransactionReference: new FormControl<string | null>(null),
    currency: new FormControl<Currency>(Currency.HNL, { nonNullable: true }),
    bankingFee: new FormControl<number | null>(0),
    securityFee: new FormControl<number | null>(0),
    notes: new FormControl<string | null>(null),
    settlementRates: this.formBuilder.array<FormGroup<ApPaymentSettlementRateFormGroup>>([]),
    allocations: this.formBuilder.array<FormGroup<ApPaymentAllocationFormGroup>>([]),
  });

  get allocations(): FormArray<FormGroup<ApPaymentAllocationFormGroup>> {
    return this.form.controls.allocations;
  }

  /** The bank's quote per currency this payment exchanges (#5135). Empty on the vast majority of payments,
   *  where every invoice is already in the payment's own money. */
  get settlementRates(): FormArray<FormGroup<ApPaymentSettlementRateFormGroup>> {
    return this.form.controls.settlementRates;
  }

  private readonly fieldLabels: Record<string, string> = {
    provider: 'ApPayment_Provider',
    paymentDate: 'ApPayment_Date',
    bankAccount: 'ApPayment_BankAccount',
    allocations: 'ApPayment_Allocations',
    settlementRates: 'ApPayment_Rate',
  };

  constructor() {
    this.statusLabels = this.enumService.getEnum('ApPaymentStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;

    this.allocations.valueChanges.subscribe(() => {
      // Order matters: selecting a foreign invoice is what creates the quote it converts through, and the
      // totals below are derived from that quote (#5135).
      this.syncSettlementRates();
      this.recomputeTotals();
    });
    this.settlementRates.valueChanges.subscribe(() => this.recomputeTotals());
    watchReceivedCoverage(this.allocations.valueChanges.pipe(map(() => this.coverageRequests())), this.poReceiptService)
      .pipe(takeUntilDestroyed())
      .subscribe((coverage) => this.receivedCoverage.set(coverage));
    // The destination decides WHO the rate authority is (#5135): its currency against the payment's says
    // whether the bank converts this wire. Changing it re-derives the quote rows and the cash they govern.
    this.form.controls.selectedProviderBankId.valueChanges.subscribe(() => {
      this.syncSettlementRates();
      this.recomputeTotals();
    });
    // The table rate is the one in force on the payment date (what the API defaults to), so moving the date
    // on a draft re-reads it for every quote row.
    this.form.controls.paymentDate.valueChanges.subscribe(() => {
      if (!this.isDraft()) return;
      for (const group of this.settlementRates.controls) this.loadDayRate(group);
    });
    this.form.controls.bankingFee.valueChanges.subscribe((v) => this.bankingFeeValue.set(v ?? 0));
    this.form.controls.securityFee.valueChanges.subscribe((v) => this.securityFeeValue.set(v ?? 0));
    // Currency drives the document list and the bank-account filter — not the other way around.
    this.form.controls.currency.valueChanges.subscribe((c) => this.onCurrencyChange(c));
    // The account the money leaves decides what currency the payment is in — not the other way round.
    this.form.controls.bankAccount.valueChanges.subscribe((id) => this.onBankAccountChange(id));

    effect(() => this.breadcrumbService.setCurrent(this.isNew() ? 'Common_New' : this.paymentNumber()), { allowSignalWrites: true });
  }

  ngOnInit(): void {
    this.loadBankAccounts();
    this.loadCashRequests();
    const id = this.route.snapshot.queryParamMap.get('id');
    const source = this.route.snapshot.queryParamMap.get('source');
    if (source === 'request') this.source.set('request');

    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadPayment(id);
      return;
    }

    // Brand-new payment: the request path starts by choosing an Approved request to fulfil.
    if (this.isRequestMode()) this.loadApprovedRequests();
  }

  // ---- helpers ----
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  currencyLabel(currency: number | null | undefined): string {
    if (currency === null || currency === undefined) return '';
    return this.currencyLabels[currency] ?? '';
  }

  statusLabelText(): string {
    return this.statusLabels[this.status()] ?? '';
  }

  statusSeverity(): 'success' | 'info' | 'secondary' {
    return this.status() === ApPaymentStatus.Posted ? 'info' : 'secondary';
  }

  // ---- bank accounts ----
  /**
   * Approved cash requests, offered as the per-line tag. Approved only: the tag records which plan the money
   * came out of, and a draft or submitted plan has not released any.
   *
   * <p>Failing quietly is deliberate — the tag is optional, so a company with no plans (or an unreachable
   * cash-request service) must not stop anyone paying an invoice.</p>
   */
  private loadCashRequests(): void {
    this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId()).subscribe({
      next: (companyId) => {
        if (!companyId) return;
        this.cashRequestService.search(companyId, CashRequestStatus.Approved).subscribe({
          next: (response) =>
            this.cashRequestOptions.set(
              (response.data ?? [])
                .filter((request) => !!request.id)
                .map((request) => ({
                  label: `${request.number} · ${new Date(request.weekStart).toLocaleDateString()}`,
                  value: request.id!,
                })),
            ),
          error: () => this.cashRequestOptions.set([]),
        });
      },
      error: () => this.cashRequestOptions.set([]),
    });
  }

  /** Read-only view of the AP document behind a row (#4789), without leaving a half-filled payment. */
  openDocumentQuickView(documentId: string | null | undefined): void {
    if (documentId) this.modalService.open(ApDocumentQuickViewModalComponent, { modalClass: 'modal_middle' }, { documentId }).subscribe();
  }

  private loadBankAccounts(): void {
    this.bankAccountService.getAll().subscribe({
      next: (response) => {
        const accounts = (response.data ?? []).filter((a) => a.status === BankAccountStatus.ACTIVE || !this.isNew());
        for (const acc of accounts) {
          if (acc.id) this.bankAccountMap.set(acc.id, acc);
        }
        // bankAccountOptions is a computed that narrows this set to the chosen currency.
        this.allBankAccounts.set(accounts);
      },
      error: () => this.allBankAccounts.set([]),
    });
  }

  /**
   * The payment takes the currency of the account it is paid from (#5135).
   *
   * <p>The one place the payment's currency is decided once an account exists. It fires on a user's pick and
   * on the patch that loads a saved payment alike, so the two can never disagree — and the API's funding
   * gate, which requires exactly this, becomes something the screen cannot violate rather than something it
   * has to filter for.</p>
   */
  private onBankAccountChange(bankAccountId: string | null): void {
    const account = bankAccountId ? this.bankAccountMap.get(bankAccountId) : null;
    if (!account || account.currency === this.form.controls.currency.value) return;
    // Emits, on purpose: onCurrencyChange reloads the payables and re-checks the destination against the
    // new currency, and both of those still have to happen.
    this.form.controls.currency.setValue(account.currency);
  }

  /** Currency drives the document list: reload the provider's docs and drop a destination it cannot reach. */
  private onCurrencyChange(currency: Currency): void {
    this.currency.set(currency);
    this.syncSettlementRates();

    // The bank is no longer dropped here — it is what SET this currency. Only the destination is re-checked,
    // and against the convertible pairs rather than an exact match: a Lempira payment into the provider's
    // dollar account is a conversion the bank makes, and it is the case the destination lock exists for.
    const destination = this.providerBanks().find((b) => b.id === this.form.controls.selectedProviderBankId.value);
    if (destination && !isConvertible(currency, destination.currency as Currency) && !this.destinationLockedToRequest()) {
      this.form.controls.selectedProviderBankId.setValue(null);
    }

    const provider = this.form.controls.provider.value;
    if (this.source() === 'document' && provider?.id) this.loadPayableInvoices(provider.id);
  }

  /** Bank, currency, last four. The currency comes from the static map, not `currencyLabels`: that is
   *  read from EnumService in the constructor, returns {} on a cold cache and fills in asynchronously, so
   *  the code silently vanished from the label whenever localStorage was not already warm. */
  private bankLabel(acc: BankAccount): string {
    const cur = currencyLabelFromApi(acc.currency);
    const last4 = (acc.bankAccountNumber ?? '').slice(-4);
    return [acc.bankName, cur, last4 ? `****${last4}` : ''].filter((s) => s).join(' · ');
  }

  // ---- provider ----
  public fetchProviders = (query: string): Observable<Business[]> =>
    this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));

  onProviderSelected(provider: Business): void {
    this.applyProviderBanks(provider);
    if (provider?.id) this.loadPayableInvoices(provider.id);
  }

  onProviderCleared(): void {
    this.providerBanks.set([]);
    this.form.controls.selectedProviderBankId.setValue(null);
    this.allocations.clear();
    this.recomputeTotals();
  }

  /**
   * Loads the provider's accounts and defaults to its primary.
   *
   * <p>The search result usually carries `providerDetails`; when it does not, one fetch by id fills it in
   * rather than leaving the picker empty and the user wondering whether the provider has no accounts.</p>
   */
  private applyProviderBanks(provider: Business | null, autoSelect = true): void {
    const banks = provider?.providerDetails?.banks ?? [];
    if (banks.length > 0 || !provider?.id) {
      this.setProviderBanks(banks, autoSelect);
      return;
    }
    this.businessService.getBusinessById(provider.id).subscribe({
      next: (response) => this.setProviderBanks(response.data?.providerDetails?.banks ?? [], autoSelect),
      error: () => this.setProviderBanks([], autoSelect),
    });
  }

  /**
   * @param autoSelect Choose the provider's primary account when none is chosen yet. True when a person
   * picks the provider — they are building the payment and a sensible default saves a click. FALSE when
   * loading a saved payment: a payment that recorded no destination did not have one, and quietly filling
   * it in on open would put an account on screen the payment never named, on a posted document at that.
   */
  private setProviderBanks(banks: ProviderBankAccount[], autoSelect = true): void {
    this.providerBanks.set(banks);
    // The banks are what tell us the destination's currency, and that currency is what decides whether the
    // wire converts (#5135) — a loaded draft restores its selection before this list arrives, so the quote
    // rows' requiredness has to be re-derived the moment it does. And the TOTALS with it: the allocation
    // rows re-price on every render, but the summary is a signal that only moves when told — this was the
    // one pricing input that changed with no recompute attached, leaving "Allocated to invoices" frozen at
    // a figure computed while the destination was still unknown.
    this.syncSettlementRates();
    this.recomputeTotals();
    if (!autoSelect || this.form.controls.selectedProviderBankId.value) return;
    // Default to the account the provider master itself calls primary, but only if it can take this
    // currency — defaulting to one the API would reject is worse than defaulting to nothing.
    // Same currency first: it needs no conversion, so it is the choice that asks the officer for nothing.
    // A convertible destination is offered in the picker but never auto-selected (#5145) — selecting one
    // silently would commit someone to recording what arrived without their having decided to convert.
    const usable = banks.filter((b) => b.id && b.currency === this.currency() && b.bankType !== ProviderBankType.Intermediary);
    const primary = usable.find((b) => b.isPrimary) ?? usable[0];
    if (primary?.id) this.form.controls.selectedProviderBankId.setValue(primary.id);
  }

  /** Alias/bank, account number, and the account's own currency. The currency is what decides whether
   *  this transfer converts at all — and therefore whether the arrival amount is required (#5145) — so
   *  leaving it off made the one field driving the rest of the form the one the picker would not show. */
  private providerBankLabel(bank: ProviderBankAccount): string {
    return [bank.alias || bank.bankName, currencyLabelFromApi(bank.currency), bank.accountNumber].filter((part) => !!part).join(' · ');
  }

  // ---- payment-request path ----
  /** Approved requests still awaiting their payment (no committed payment yet) — the choices for the request path. */
  readonly requestOptions = computed<RequestOption[]>(() =>
    this.approvedRequests().map((r) => ({
      // A request that mixes currencies has no native total to offer (#5135) — it shows what it comes to
      // in the functional currency instead, which is the figure every line converts to. Showing the old
      // `totalAmount` here meant offering PR-2026-000001 as "12,000 HNL", which is 10,000 Lempiras and
      // 2,000 dollars added together.
      label: `${r.number} · ${r.providerLegalName} · ${this.requestTotalLabel(r)}`,
      value: r.id ?? '',
    })),
  );

  /** "2,372.27 USD" — the request's own total where it has one, its functional total where it does not. */
  private requestTotalLabel(request: ApPaymentRequest): string {
    const mixed = request.totalAmount == null;
    const amount = mixed ? request.totalFunctionalAmount : request.totalAmount!;
    const currency = mixed ? request.functionalCurrency : request.currency;
    const value = (amount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${value} ${this.currencyLabel(currency)}${mixed ? ` · ${this.t('ApPayment_Mixed', 'mixed')}` : ''}`;
  }

  private loadApprovedRequests(): void {
    this.startLoading();
    this.requestService
      .search({ status: ApPaymentRequestStatus.Approved }, 1, 100)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => this.approvedRequests.set(response.data?.items ?? []),
        error: () => this.approvedRequests.set([]),
      });
  }

  /** Open (or spawn) the 1:1 Draft payment for the chosen Approved request, then drop into the normal payment form. */
  onRequestSelected(requestId: string | null): void {
    if (!requestId) return;
    this.startLoading();
    this.requestService
      .createOrGetPayment(requestId)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const payment = response.data;
          if (!payment?.id) return;
          this.isNew.set(false);
          this.currentId.set(payment.id);
          this.router.navigate([], { queryParams: { id: payment.id }, replaceUrl: true });
          this.patchFromPayment(payment);
        },
        error: (error) => console.error('Error opening payment for request:', error),
      });
  }

  // ---- payable invoices / allocations ----
  /** Build one GROUPED allocation row per payable document (#4516): a base document carries its notes inside
   * the resumen, a standalone note is its own row. The single amount is distributed doc-first at submit, so the
   * payload stays one allocation per document. Uses the aggregated endpoint (true net, real withholding).
   *
   * <p>With `restore` (a reloaded DRAFT's saved allocations), the saved rows are folded back onto the payable
   * rows they came from: ticked, at their saved settlement, with the document's TRUE net payable as the cap —
   * and every other open invoice offered unticked. The flat snapshot used to stand in for this, which showed
   * the draft as if it were history: net payable equal to what was allocated, and the rest of the provider's
   * open debt invisible.</p> */
  private loadPayableInvoices(providerId: string, restore?: ApPaymentAllocation[]): void {
    this.startLoading();
    // Every currency, not just the payment's (#5135). A payment settles a document in another currency by
    // converting the DISCHARGE — the money itself never converts — so filtering the list to the payment's
    // currency hid invoices that are perfectly payable, and a provider billing in both left half its debt
    // invisible on whichever payment you happened to be making. What is offered is narrowed below to the
    // pairs a conversion is defined for, which is the same rule the API settles by.
    this.documentService
      .getPayableAggregated(providerId, null)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.allocations.clear();
          this.payableByDoc.clear();
          this.expandedRows = {};
          // Reloading a draft: its saved allocations keyed by document, waiting to be folded back onto the
          // payable rows they came from. Whatever is still here after the loop is a document the payable
          // list no longer offers — it gets the flat snapshot row instead.
          const savedByDoc = restore ? new Map(restore.map((a) => [a.documentReference, a])) : null;
          this.restoredAdvanceByDoc = restore ? new Map() : null;
          const settleable = (response.data ?? []).filter((p) =>
            isConvertible(this.currency(), (p.document.currency ?? this.currency()) as Currency),
          );
          for (const p of settleable) {
            const baseId = p.document.id ?? '';
            this.payableByDoc.set(baseId, p);
            const netDefault = payableRowDefault(p);
            const isCredit = !isPayableBaseGroup(p) && p.document.documentType === ApDocumentType.CreditNote;
            // #4488/#4842 — the payment post rejects a document with no posted Constancia, so it must not
            // arrive here preselected. Shown disabled with the reason rather than dropped.
            const blocked = p.withholdingSatisfied === false;
            // A document in another currency is offered but never pre-ticked. Its cash cost moves with the
            // rate, so silently including one would change what the payment comes to without anyone having
            // chosen to convert — the same reasoning that keeps a convertible DESTINATION off the default.
            const foreign = (p.document.currency ?? this.currency()) !== this.currency();
            // The draft's saved rows for this GROUP, re-folded (#4516 in reverse): the save distributed the
            // group's one settlement doc-first across base + debit notes, so summing the members' settled amounts
            // recovers exactly the figure the user typed. Credit notes are not members (#6324): open credit is
            // its own row.
            const memberIds = [baseId, ...p.debitNotes.map((n) => n.id ?? '')];
            const members = savedByDoc ? memberIds.map((id) => savedByDoc.get(id)).filter((a): a is ApPaymentAllocation => !!a) : [];
            for (const member of members) savedByDoc!.delete(member.documentReference);
            const hasSaved = members.length > 0;
            const savedSettlement =
              Math.round(
                members.reduce((sum, a) => sum + (a.settledAmount ?? (a.allocatedAmount ?? 0) + (a.advanceAppliedAmount ?? 0)), 0) * 100,
              ) / 100;
            const savedAdvance = Math.round(members.reduce((sum, a) => sum + (a.advanceAppliedAmount ?? 0), 0) * 100) / 100;
            if (hasSaved) this.restoredAdvanceByDoc?.set(baseId, savedAdvance);
            this.allocations.push(
              this.formBuilder.group<ApPaymentAllocationFormGroup>({
                documentReference: new FormControl<string>(baseId, { nonNullable: true }),
                documentNumber: new FormControl<string>(p.document.number ?? '', { nonNullable: true }),
                dueDate: new FormControl<string | null>(p.document.dueDate ?? null),
                netPayable: new FormControl<number>(netDefault, { nonNullable: true }),
                // Restoring: only what the draft holds comes back ticked — pre-ticking the REST would grow
                // the payment's amount just by opening it. Building fresh: the usual defaults.
                selected: new FormControl<boolean>(hasSaved || (!savedByDoc && !blocked && !foreign), { nonNullable: true }),
                // A saved allocation is never re-gated here (same policy as the flat rows): the post
                // re-checks it server-side, which is the authority.
                withholdingBlocked: new FormControl<boolean>(!hasSaved && blocked, { nonNullable: true }),
                // allocatedAmount here is the SETTLEMENT (net), in the DOCUMENT's currency (#5144);
                // buildAllocations sends it as settledAmount and the API derives the cash.
                allocatedAmount: new FormControl<number>(hasSaved ? savedSettlement : netDefault, { nonNullable: true }),
                isCredit: new FormControl<boolean>(isCredit, { nonNullable: true }),
                documentCurrency: new FormControl<Currency>(p.document.currency ?? this.currency(), { nonNullable: true }),
                documentExchangeRate: new FormControl<number>(p.document.exchangeRate ?? 1, { nonNullable: true }),
                docWithholding: new FormControl<number>(p.withheldAmount ?? 0, { nonNullable: true }),
                advanceToUse: new FormControl<number>(hasSaved ? savedAdvance : 0, { nonNullable: true }),
                advanceAvailable: new FormControl<number>(0, { nonNullable: true }),
                // Optional cost-centre style tag; nothing preselects it on a payment built from scratch.
                cashRequestReference: new FormControl<string | null>(
                  members.find((a) => a.cashRequest?.reference)?.cashRequest?.reference ?? null,
                ),
                fiscalNumber: new FormControl<string>(p.document.fiscalNumber ?? '', { nonNullable: true }),
              }),
            );
          }
          // Whatever the draft still holds that the payable list no longer offers — settled by another
          // payment since the save, most likely — stays visible as a flat row: hiding it would misreport
          // what this draft does, and the post is what refuses it if it truly cannot go through.
          if (savedByDoc) {
            for (const orphan of savedByDoc.values()) {
              this.restoredAdvanceByDoc?.set(orphan.documentReference, orphan.advanceAppliedAmount ?? 0);
              this.allocations.push(this.flatAllocationRow(orphan, this.currency()));
            }
          }
          // The grid is whole again — one sync now, with the rows the requiredness is actually decided by.
          // (The per-push syncs above bailed while restoringAllocations held them off.)
          this.restoringAllocations = false;
          this.syncSettlementRates();
          // Fetch advance available on these invoices' POs, then auto-fill "advance to use" (#4349).
          this.loadAdvances();
        },
        error: () => {
          this.allocations.clear();
          this.payableByDoc.clear();
          this.restoredAdvanceByDoc = null;
          this.restoringAllocations = false;
          this.recomputeTotals();
        },
      });
  }

  /** Fetch the posted advances (Anticipos) available on the loaded invoices' POs, then auto-fill each row's
   * "advance to use" to the shared running availability (#4349). Direct-payment path only. */
  private loadAdvances(): void {
    this.advanceAvailByPo.clear();
    const poRefs = new Set<string>();
    for (const p of this.payableByDoc.values()) {
      if (!isPayableBaseGroup(p)) continue;
      for (const k of p.document.purchaseOrderKeys ?? []) if (k.reference) poRefs.add(k.reference);
    }
    if (poRefs.size === 0) {
      this.applyAdvanceCaps();
      return;
    }
    this.advanceService.getPostedByPurchaseOrders([...poRefs]).subscribe({
      next: (response) => {
        for (const advance of response.data ?? []) {
          for (const alloc of advance.allocations ?? []) {
            if (poRefs.has(alloc.purchaseOrderReference)) {
              this.advanceAvailByPo.set(
                alloc.purchaseOrderReference,
                (this.advanceAvailByPo.get(alloc.purchaseOrderReference) ?? 0) + (alloc.remainingAmount ?? 0),
              );
            }
          }
        }
        this.applyAdvanceCaps();
      },
      error: () => this.applyAdvanceCaps(),
    });
  }

  /** Per base-group row, set the advance cap = min(base balance, settlement, PO advance still available given
   * earlier rows' use) and auto-fill "advance to use" to it — a shared running balance so several invoices of
   * one PO can't over-draw (#4349). On a reloaded draft the saved drawdowns are RESTORED rather than
   * auto-filled (clamped to the cap: availability may have moved since the save), and they claim the shared
   * balance first — they are commitments being reopened, not defaults being offered, so an unticked row must
   * not starve them of it. */
  private applyAdvanceCaps(): void {
    const running = new Map(this.advanceAvailByPo);
    const restored = this.restoredAdvanceByDoc;
    this.restoredAdvanceByDoc = null;
    const rows = [...this.allocations.controls];
    const ordered = restored
      ? [
          ...rows.filter((r) => restored.has(r.controls.documentReference.value)),
          ...rows.filter((r) => !restored.has(r.controls.documentReference.value)),
        ]
      : rows;
    for (const row of ordered) {
      const docRef = row.controls.documentReference.value;
      const p = this.payableByDoc.get(docRef);
      if (!p || !isPayableBaseGroup(p)) {
        // A flat straggler row keeps the drawdown its draft stored (the post re-validates it) — zeroing it
        // here would silently change the cash of a payment nobody edited.
        const keep = Math.max(restored?.get(docRef) ?? 0, 0);
        row.controls.advanceAvailable.setValue(keep, { emitEvent: false });
        row.controls.advanceToUse.setValue(keep, { emitEvent: false });
        continue;
      }
      const poRefs = (p.document.purchaseOrderKeys ?? []).map((k) => k.reference).filter((r): r is string => !!r);
      let avail = 0;
      for (const po of poRefs) avail += running.get(po) ?? 0;

      const baseBalance = p.document.balanceDue ?? p.document.totalAmount ?? 0;
      const settlement = Math.max(row.controls.allocatedAmount.value ?? 0, 0);
      const cap = Math.round(Math.min(baseBalance, settlement, avail) * 100) / 100;
      const use = restored && restored.has(docRef) ? Math.min(Math.max(restored.get(docRef) ?? 0, 0), cap) : cap;
      row.controls.advanceAvailable.setValue(cap, { emitEvent: false });
      row.controls.advanceToUse.setValue(use, { emitEvent: false });

      let need = use;
      for (const po of poRefs) {
        if (need <= 0) break;
        const a = running.get(po) ?? 0;
        const take = Math.min(a, need);
        running.set(po, a - take);
        need -= take;
      }
    }
    this.recomputeTotals();
  }

  /**
   * Cash for a row, in the PAYMENT's currency: (settlement − advance) × the document's rate (#5144, #4349).
   *
   * <p>The rate is 1 whenever the document is in the payment's currency, which is almost always — so this
   * reads as it always did until the day an invoice is genuinely foreign. Rounded once at the end, matching
   * `SettlementConversion.CashFor` server-side; the API's figure is authoritative and this only has to agree
   * with it well enough that the total on screen is the total that posts.</p>
   */
  cashToPay(row: FormGroup<ApPaymentAllocationFormGroup>): number {
    const settlement = row.controls.allocatedAmount.value ?? 0;
    const advance = Math.min(row.controls.advanceToUse.value ?? 0, Math.max(settlement, 0));
    return Math.round((settlement - advance) * this.rateFor(row) * 100) / 100;
  }

  // ---- the bank's rate for this payment (#5135) ----

  /** The destination the payment itself froze at save (#5143) — the banking snapshot's account and
   *  currency. The provider master is a lookup that can lag or fail; where this payment's money goes is a
   *  fact the payment records, and pricing must not wait on (or break with) the master. */
  private readonly savedDestination = signal<{ id: string; currency: Currency } | null>(null);

  /** The currency the wire lands in — the chosen provider account's. Null until one is chosen, which
   *  also means "no conversion at the bank": nothing to convert into.
   *
   *  <p>Resolved from the live bank list first, then from the payment's own frozen snapshot. The snapshot
   *  matters on a reloaded draft: the bank list arrives late (a background fetch), and while it was the
   *  only source every row silently re-priced at the invoice's booking rate — the field showed the bank's
   *  28 while the math ran at 26.86.</p> */
  private destinationCurrency(): Currency | null {
    const id = this.form.controls.selectedProviderBankId.value;
    if (!id) return null;
    const bank = this.providerBanks().find((b) => b.id === id);
    if (bank && bank.currency != null) return bank.currency as Currency;
    const saved = this.savedDestination();
    return saved && saved.id === id ? saved.currency : null;
  }

  /**
   * The currencies this payment has to quote, mirroring `PaymentSettlementRates.RequiredQuotes`. Note what
   * is absent: the invoices. A foreign invoice on an unconverted wire prices its own discharge at its
   * booking rate — the bank converts nothing, so there is no rate to ask anyone for.
   *
   * <p>What remains: the payment's own currency when it is not USD (the entry still translates into the
   * books), and the bank-converted wire when the destination account holds a different money than the
   * payment — quoted on its non-USD side, since every pair routes through the pivot.</p>
   */
  /**
   * The destination currency <i>for conversion purposes</i> — null when no wire is executed.
   *
   * <p>A payment the advance covers in full moves no money (#4349), so the bank converts nothing and there
   * is no wire rate for anyone to have been given. The destination account is still on the record; it just
   * stops being a rate authority. Without this the screen demanded "the rate the bank gave you" for a
   * transfer that never happens, and the required field made the payment impossible to post.</p>
   */
  private convertingDestinationCurrency(): Currency | null {
    return this.fullyCoveredByAdvance() ? null : this.destinationCurrency();
  }

  private requiredQuoteCurrencies(): Currency[] {
    const payment = this.currency();
    const destination = this.convertingDestinationCurrency();
    const required = new Set<Currency>();
    if (payment !== Currency.USD) required.add(payment);
    if (destination !== null && destination !== payment) {
      required.add(payment);
      required.add(destination);
    }
    required.delete(Currency.USD);
    return [...required].sort((a, b) => a - b);
  }

  /** True when this row is the BANK's wire rate — payment currency to destination currency — which is
   *  required and never defaulted: only the person who spoke to the bank knows it. */
  isBankQuote(group: FormGroup<ApPaymentSettlementRateFormGroup>): boolean {
    const payment = this.currency();
    const destination = this.convertingDestinationCurrency();
    if (destination === null || destination === payment) return false;
    return group.controls.currency.value === (payment !== Currency.USD ? payment : destination);
  }

  /**
   * Replaces the panel's rows with what the payment actually stores (#5135) — on load, and again after
   * every save, because the server refines what was sent: it defaults a translation row the screen left
   * blank, prunes what the payment no longer exchanges, and derives the rest. Without this the panel kept
   * showing the pre-save form state and the saved rate looked unsaved.
   *
   * <p>loadDayRate still runs per row to fill dayRateToUsd, which is only used to show whether the frozen
   * figure was an override.</p>
   */
  private applySettlementRates(rates: ApPaymentSettlementRate[]): void {
    this.settlementRates.clear({ emitEvent: false });
    for (const quote of rates) {
      const group = this.formBuilder.group<ApPaymentSettlementRateFormGroup>({
        currency: new FormControl<Currency>(quote.currency, { nonNullable: true }),
        rateToUsd: new FormControl<number | null>(quote.rateToUsd ?? null),
        dayRateToUsd: new FormControl<number | null>(null),
      });
      this.settlementRates.push(group, { emitEvent: false });
      this.loadDayRate(group);
    }
    // The in-flight keystrokes are superseded by what is now committed and stored.
    this.typingRates.set({});
    this.recomputeTotals();
  }

  /** Adds a quote for every conversion this payment now makes and drops the ones it no longer does, so a
   *  rate left over from a changed destination can never govern a cash amount nobody meant it to. Draft
   *  only: a posted payment's rates are history, restored from what it stored, never re-derived. */
  private syncSettlementRates(): void {
    if (!this.isDraft()) return;
    // A reloaded draft's rows are still on their way — see restoringAllocations. Requiredness cannot be
    // decided from an empty grid, so leave the restored rates exactly as stored; the rows re-sync the
    // moment they land.
    if (this.restoringAllocations) return;
    // A destination is chosen but its currency is unknowable — the bank list has not arrived and the
    // payment carries no snapshot for it. Requiredness cannot be decided, so leave the rows exactly as
    // they are: pruning now would erase a restored saved rate on a race, and setProviderBanks re-syncs
    // the instant the list lands.
    const chosenBankId = this.form.controls.selectedProviderBankId.value;
    if (chosenBankId && this.destinationCurrency() === null) return;
    // A payment with nothing on it converts nothing yet — the rows are what create the need for a rate.
    const required = this.allocations.length > 0 ? this.requiredQuoteCurrencies() : [];

    for (let i = this.settlementRates.length - 1; i >= 0; i--) {
      if (!required.includes(this.settlementRates.at(i).controls.currency.value)) this.settlementRates.removeAt(i);
    }

    for (const currency of required) {
      if (this.settlementRates.controls.some((g) => g.controls.currency.value === currency)) continue;
      const group = this.formBuilder.group<ApPaymentSettlementRateFormGroup>({
        currency: new FormControl<Currency>(currency, { nonNullable: true }),
        rateToUsd: new FormControl<number | null>(null),
        dayRateToUsd: new FormControl<number | null>(null),
      });
      this.settlementRates.push(group);
      this.loadDayRate(group);
    }

    // Requiredness follows the destination, and the destination can change after the row exists — the same
    // HNL row is an optional books translation on an HNL→HNL wire and the bank's required quote the moment
    // the destination flips to USD.
    for (const group of this.settlementRates.controls) {
      group.controls.rateToUsd.setValidators(this.isBankQuote(group) ? [Validators.required, Validators.min(0.000001)] : []);
      group.controls.rateToUsd.updateValueAndValidity({ emitEvent: false });
    }
  }

  /** Fetches the rate in force on the PAYMENT date for a quote row — the same lookup
   *  ResolveSettlementRatesAsync defaults to (CurrencyConverter.GetRateToUsdAsync on PaymentDate), not
   *  today's, so a backdated payment previews the rate it will post at. A books-translation row is
   *  prefilled with it — the right answer when nobody has been told otherwise. The BANK's wire row is never
   *  prefilled: the table records an average, the bank charges this wire's rate, and a prefilled field
   *  invites approving a number nobody was actually given. The table's figure still lands in the hint, as a
   *  sanity reference. */
  private loadDayRate(group: FormGroup<ApPaymentSettlementRateFormGroup>): void {
    const asOf = tableRateDate(this.form.controls.paymentDate.value);
    // What the row was prefilled with before this lookup. A translation row still holding it was following
    // the table, not overridden, so it follows the table to the new date too; a typed rate is left alone.
    const previousDay = group.controls.dayRateToUsd.value;
    this.exchangeRateService.getRateInForce(group.controls.currency.value, asOf).subscribe({
      next: (response) => {
        // The average (#5221), which is what ResolveSettlementRatesAsync defaults to. Prefilling the
        // buy/compra side instead would make every untouched row differ from the server's default —
        // and the API derives isManual by exactly that comparison, so each one would be stamped as a
        // rate the user had entered by hand.
        const day = response.data?.averageRateToUSD ?? response.data?.rateToUSD ?? 0;
        if (day <= 0) return;
        group.controls.dayRateToUsd.setValue(day, { emitEvent: false });
        if (!this.isBankQuote(group) && followsTableRate(group.controls.rateToUsd.value, previousDay)) {
          group.controls.rateToUsd.setValue(day);
        }
      },
      // No rate on file for that date leaves the hint empty, which is the honest state: the previous date's
      // figure no longer applies, and the cash cannot be computed at a fabricated 1.
      error: () => group.controls.dayRateToUsd.setValue(null, { emitEvent: false }),
    });
  }

  /**
   * What is being typed into a rate field RIGHT NOW, per currency (#5135).
   *
   * <p>PrimeNG's number input commits to the form control on blur or Enter, not per keystroke — so while
   * the user is still in the field, the control says null and every row priced by it reads "enter the bank
   * rate" with the digits sitting right there on screen. This map carries the in-flight value so the rows
   * price as the rate is typed; the control's committed value takes over the moment focus leaves.</p>
   */
  private readonly typingRates = signal<Partial<Record<Currency, number>>>({});

  /** Live keystrokes from a rate field — see {@link typingRates}. */
  onRateInput(group: FormGroup<ApPaymentSettlementRateFormGroup>, value: number | string | null): void {
    const currency = group.controls.currency.value;
    const rate = typeof value === 'number' ? value : 0;
    this.typingRates.update((rates) => ({ ...rates, [currency]: rate }));
    this.recomputeTotals();
  }

  private quoteFor(currency: Currency): number {
    const committed = this.settlementRates.controls.find((g) => g.controls.currency.value === currency)?.controls.rateToUsd.value ?? 0;
    // Committed wins — it is what saves. The in-flight value only fills the gap while the field is focused;
    // an emptied field types through as 0, so clearing the rate un-prices the rows immediately too.
    return committed > 0 ? committed : (this.typingRates()[currency] ?? 0);
  }

  /** Payment → functional, the divisor every discharge converts through. 1 for a dollar payment: USD is
   *  both the pivot the rate table is stored against and the functional currency here. Null when the quote
   *  this payment needs has not arrived or been typed yet — the caller shows nothing rather than a figure
   *  converted at a rate nobody chose. */
  private paymentToFunctionalRate(): number | null {
    if (this.currency() === Currency.USD) return 1;
    const multiplier = toFunctionalMultiplier(this.quoteFor(this.currency()));
    return multiplier > 0 ? multiplier : null;
  }

  /**
   * The document → payment multiplier for a row, mirroring `SettlementConversion.RateToPayment` (#5135).
   *
   * <p>1 when the two currencies agree — no exchange, no rate. Otherwise a document → functional rate over
   * the payment's, which cancels the functional currency and leaves document → payment.</p>
   *
   * <p>The numerator's authority depends on whether the bank converts (the two cases of #5135): a dollar
   * document is 1 by definition; a document in the currency the wire LANDS in is priced by the bank's
   * quote, and shows 0 — visibly wrong, self-healing — until that quote is typed; any other foreign
   * document is priced by its own booking rate, because the bank never touches its currency and the
   * invoice is the only authority there is. Same law as the API.</p>
   */
  rateFor(row: FormGroup<ApPaymentAllocationFormGroup>): number {
    const documentCurrency = row.controls.documentCurrency.value;
    const payment = this.currency();
    if (documentCurrency === payment) return 1;

    const paymentRate = this.paymentToFunctionalRate();
    if (paymentRate === null) return 0;

    let documentRate: number;
    const destination = this.destinationCurrency();
    if (documentCurrency === Currency.USD) {
      documentRate = 1;
    } else if (destination !== null && destination !== payment && documentCurrency === destination) {
      documentRate = toFunctionalMultiplier(this.quoteFor(documentCurrency));
    } else {
      documentRate = row.controls.documentExchangeRate.value ?? 0;
    }
    return documentRate > 0 ? documentRate / paymentRate : 0;
  }

  /** Where a converted row's rate comes from, named beside the figure — the invoice itself on an
   *  unconverted wire, the bank on a converted one, the payment's own rate when only the payment side
   *  converts. Empty for a same-currency row, which has no rate to attribute. */
  rateSourceLabel(row: FormGroup<ApPaymentAllocationFormGroup>): string {
    const documentCurrency = row.controls.documentCurrency.value;
    const payment = this.currency();
    if (documentCurrency === payment) return '';
    const destination = this.destinationCurrency();
    if (destination !== null && destination !== payment) return this.t('ApPayment_RateSource_Bank', 'bank rate');
    if (documentCurrency === Currency.USD) return this.t('ApPayment_RateSource_Payment', "payment's rate");
    return this.t('ApPayment_RateSource_Invoice', 'invoice rate');
  }

  /** The band this currency plausibly trades in, for the field's hint. Null where none is configured. */
  rateBoundsFor(group: FormGroup<ApPaymentSettlementRateFormGroup>): { min: number; max: number } | null {
    return exchangeRateBoundsFor(group.controls.currency.value);
  }

  /** A rate outside its band — almost always a slipped decimal, which books cleanly and is invisible
   *  afterwards. The API refuses it too; this only says so earlier. */
  rateOutOfBounds(group: FormGroup<ApPaymentSettlementRateFormGroup>): boolean {
    return !isPlausibleExchangeRate(group.controls.currency.value, group.controls.rateToUsd.value);
  }

  /** True once the rate differs from the day's — the cue that somebody overrode it. */
  rateIsOverridden(group: FormGroup<ApPaymentSettlementRateFormGroup>): boolean {
    const day = group.controls.dayRateToUsd.value;
    const typed = group.controls.rateToUsd.value;
    return day != null && typed != null && day > 0 && typed !== day;
  }

  /** What this row's rate is FOR, composed here because the translate pipe takes no parameters. */
  rateAppliesMessage(group: FormGroup<ApPaymentSettlementRateFormGroup>): string {
    if (this.isBankQuote(group)) {
      const destination = this.destinationCurrency();
      return this.t('ApPayment_Rate_BankWire', 'The bank converts {from} to {to} on this wire — enter the rate it gave you.')
        .replace('{from}', this.currencyLabels[this.currency()] ?? '')
        .replace('{to}', destination !== null ? (this.currencyLabels[destination] ?? '') : '');
    }
    // Not a bank rate: it only carries the entry into the books, which used to happen silently at
    // whatever the table said on posting day.
    return this.t('ApPayment_Rate_Books', 'This payment posts to the books (USD) at this rate.');
  }

  rateOutOfBoundsMessage(group: FormGroup<ApPaymentSettlementRateFormGroup>): string {
    const bounds = this.rateBoundsFor(group);
    if (!bounds) return this.t('ApPayment_Rate_Invalid', 'Enter a rate greater than zero.');
    return this.t('ApPayment_Rate_OutOfBounds', 'That is outside {min}–{max} — check the decimal point.')
      .replace('{min}', bounds.min.toFixed(2))
      .replace('{max}', bounds.max.toFixed(2));
  }

  rateOverriddenMessage(group: FormGroup<ApPaymentSettlementRateFormGroup>): string {
    const day = group.controls.dayRateToUsd.value ?? 0;
    return this.t('ApPayment_Rate_Overridden', "Overridden — the day's rate is {day}.").replace('{day}', day.toFixed(4));
  }

  /** The panel's footer, matched to what the rates are actually doing: pricing a wire the bank converts,
   *  or only carrying the entry into the books. */
  ratePanelHint(): string {
    const bankConverts = this.settlementRates.controls.some((g) => this.isBankQuote(g));
    if (!bankConverts) {
      return this.t(
        'ApPayment_Rate_Hint_Books',
        'Nothing converts at the bank here — this rate only translates the entry into the books (USD).',
      );
    }
    return this.t(
      'ApPayment_Rate_Hint',
      'This is what the bank gave you, and it sets the cash. Each invoice is still cleared at the rate it was booked at — the difference posts as an exchange gain or loss.',
    );
  }

  /** Restores the day's rate on a quote the user has edited. */
  resetRate(group: FormGroup<ApPaymentSettlementRateFormGroup>): void {
    const day = group.controls.dayRateToUsd.value;
    if (day != null && day > 0) group.controls.rateToUsd.setValue(day);
  }

  /** The quotes as the API takes them. A blank rate is left out rather than sent as 0 — the server then
   *  defaults that currency to the day's rate, which is what a blank means. */
  private buildSettlementRates(): ApPaymentSettlementRateInsert[] {
    return this.settlementRates.controls
      .filter((g) => (g.controls.rateToUsd.value ?? 0) > 0)
      .map((g) => ({ currency: g.controls.currency.value, rateToUsd: g.controls.rateToUsd.value! }));
  }

  /** True when this row's invoice is in a different currency than the payment — the cue for the grid to show
   *  the discharge and the derived cash side by side rather than one number (#5144). */
  isConverted(row: FormGroup<ApPaymentAllocationFormGroup>): boolean {
    return row.controls.documentCurrency.value !== this.currency();
  }

  /** Withholding already applied to this document by a posted Constancia de Retención — informational: it
   * already reduced the balance due, so the payment settles the net directly and it is not re-deducted (#4515). */
  allocationWithholding(row: FormGroup<ApPaymentAllocationFormGroup>): number {
    return row.controls.docWithholding.value ?? 0;
  }

  /** The payable aggregate for a base-document row (undefined for a loaded/flat row without aggregate data). */
  payableFor(docId: string | null | undefined): PayableDocument | undefined {
    return docId ? this.payableByDoc.get(docId) : undefined;
  }

  toggleExpand(docId: string | null | undefined): void {
    if (docId) this.expandedRows[docId] = !this.expandedRows[docId];
  }

  /** Every allocation that would pay something, at what it would pay now — only while this is a draft.
   * The allocated amount is the discharge in the document's own currency, which is what the API expects. */
  private coverageRequests(): PoReceiptCoverageRequest[] {
    if (!this.isDraft()) return [];
    return this.allocations.controls
      .filter((row) => !row.controls.isCredit.value && !row.controls.withholdingBlocked.value)
      .map((row) => ({
        documentReference: row.controls.documentReference.value,
        amount: row.controls.allocatedAmount.value ?? 0,
        // Selected allocations are checked together; the rest as candidates (see the request screen).
        candidate: !row.controls.selected.value,
      }));
  }

  /** A row whose amount is adjusted by a note, withholding, or an existing partial payment — the flag tooltip. */
  adjustmentTooltip(docId: string | null | undefined): string {
    const p = this.payableFor(docId);
    if (!p) return '';
    const parts: string[] = [];
    for (const dn of p.debitNotes) parts.push(`+ ${this.t('ApDocument_DebitNote', 'Debit note')} ${dn.number ?? ''}`);
    for (const cn of p.creditNotes) parts.push(`− ${this.t('ApDocument_CreditNote', 'Credit note')} ${cn.number ?? ''}`);
    if (p.withheldAmount > 0) parts.push(`− ${this.t('ApDocument_Withholding', 'Withholding')} ${p.withheldAmount.toFixed(2)}`);
    if ((p.document.paidAmount ?? 0) > 0) parts.push(this.t('ApPayment_PartialPaymentExists', 'A partial payment already exists'));
    return parts.join('\n');
  }

  private recomputeTotals(): void {
    let cash = 0;
    let advance = 0;
    let selected = 0;
    for (const row of this.allocations.controls) {
      if (row.controls.selected.value) {
        const settlement = row.controls.allocatedAmount.value ?? 0;
        const adv = Math.min(row.controls.advanceToUse.value ?? 0, Math.max(settlement, 0));
        // Converted per row (#5144): the payment amount is cash in the PAYMENT's currency, and the rows it
        // comes from may each be stated in their own.
        cash += (settlement - adv) * this.rateFor(row);
        advance += adv;
        selected++;
      }
    }
    // amount = total CASH out (settlement − advance); the advance clears from the prepaid asset (#4349).
    this.amount.set(Math.round(cash * 100) / 100);
    this.totalAdvance.set(Math.round(advance * 100) / 100);
    this.selectedCount.set(selected);
    this.totalInvoices.set(this.allocations.length);
    this.syncBankAccountRequirement();

    // Whether the bank converts follows the cash too: an advance grown to cover the whole settlement
    // retires the wire rate along with the wire. Only on an actual flip — the quote rows are an INPUT to
    // the totals above (rateFor), and their own valueChanges calls this method, so re-syncing them every
    // time would have it feeding itself.
    const covered = this.fullyCoveredByAdvance();
    if (covered !== this.wasFullyCoveredByAdvance) {
      this.wasFullyCoveredByAdvance = covered;
      this.syncSettlementRates();
    }
  }

  /**
   * The bank account is required only while there is cash to move (#4349).
   *
   * <p>An advance that covers the whole settlement leaves nothing to pay: the entry is Dr AP / Cr Anticipo,
   * no money leaves any account, and the API posts it with no bank line and no statement movement. Keeping
   * the field required would make the one payment that needs no account impossible to post — and naming an
   * account would record a transfer out of it that never happened.</p>
   */
  private syncBankAccountRequirement(): void {
    const required = this.amount() > 0;
    if (required === this.bankAccountRequired) return;
    this.bankAccountRequired = required;

    const control = this.form.controls.bankAccount;
    if (required) control.addValidators(Validators.required);
    else control.removeValidators(Validators.required);
    control.updateValueAndValidity({ emitEvent: false });
  }

  // ---- load existing ----
  private loadPayment(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.patchFromPayment(response.data);
        },
        error: (error) => console.error('Error loading AP payment:', error),
      });
  }

  private patchFromPayment(data: ApPayment): void {
    this.status.set(data.status);
    this.currency.set(data.currency);
    this.paymentNumber.set(data.number ?? '');
    this.headerTitle.set(`${this.t('ApPayment', 'Payment')} ${data.number ?? ''}`.trim());
    this.attachments.set(data.attachments ?? []);
    this.autoCreated.set(data.autoCreated ?? false);
    this.requestId.set(data.apPaymentRequestReference ?? null);
    this.journalEntryReference.set(data.journalEntryReference ?? null);
    this.journalEntryDocumentNumber.set(data.journalEntryDocumentNumber ?? null);

    this.form.patchValue(
      {
        provider: {
          id: data.providerReference,
          name: data.providerName ?? '',
          code: data.providerCode,
          businessTypes: [BusinessEntityType.Provider],
        } as Business,
        paymentDate: data.paymentDate ? new Date(data.paymentDate) : null,
        bankAccount: data.bankAccountReference,
        selectedProviderBankId: data.selectedProviderBankId ?? null,
        bankTransactionReference: data.bankTransactionNumber ?? null,
        currency: data.currency,
        bankingFee: data.transferFee ?? 0,
        securityFee: data.securityTax ?? 0,
        notes: data.notes ?? null,
      },
      { emitEvent: false },
    );
    this.bankingFeeValue.set(data.transferFee ?? 0);
    this.securityFeeValue.set(data.securityTax ?? 0);

    // The frozen destination (#5143), seeded BEFORE anything prices: the quote rows and every allocation
    // convert according to where this wire lands, and the provider master that could also answer loads
    // late — or not at all.
    const snapshotCurrency = data.banking?.paymentBank?.currency;
    this.savedDestination.set(
      data.selectedProviderBankId && snapshotCurrency != null
        ? { id: data.selectedProviderBankId, currency: snapshotCurrency as Currency }
        : null,
    );

    // The quotes this draft was frozen at (#5135), restored BEFORE the rows that convert through them —
    // and restored rather than re-fetched, because what Treasury approved was the rate the bank gave them
    // that day, not today's.
    this.applySettlementRates(data.settlementRates ?? []);

    this.allocations.clear();
    this.payableByDoc.clear();
    this.expandedRows = {};
    // A DRAFT is a decision still in progress: rebuild the same screen it was built on — the provider's
    // whole payable list, with the saved rows folded back on (ticked, at their saved amounts, capped by the
    // TRUE net payable) and every other open invoice offered unticked. The flat snapshot below is for
    // payments whose allocations are no longer choices: posted (they are history, and their documents may
    // have left the payables) or request-owned (the request decided them, and the rows are locked anyway).
    const editableDraft = data.status === ApPaymentStatus.Draft && !data.apPaymentRequestReference && !!data.providerReference;
    if (editableDraft) {
      this.restoringAllocations = true;
      this.loadPayableInvoices(data.providerReference, data.allocations ?? []);
    } else {
      for (const alloc of data.allocations ?? []) this.allocations.push(this.flatAllocationRow(alloc, data.currency));
      this.recomputeTotals();
    }

    if (this.status() !== ApPaymentStatus.Draft) {
      this.form.disable({ emitEvent: false });
    } else {
      // A loaded draft is a persisted one, and its allocations are this provider's documents — changing
      // the provider would invalidate every line, so the field is locked from the first save on.
      this.form.controls.provider.disable({ emitEvent: false });
    }
    if (this.status() === ApPaymentStatus.Draft && this.fromRequest()) {
      // A request-spawned draft: allocations are owned by the request — only bank/date/notes are editable.
      this.allocations.disable({ emitEvent: false });
      // And WHERE the money goes is owned by the request too: the destination is part of what was approved,
      // not a choice left to whoever executes it. Only when the request actually named one — a request
      // approved without a destination leaves the field open, which is the case it is optional for.
      if (data.selectedProviderBankId) {
        this.form.controls.selectedProviderBankId.disable({ emitEvent: false });
        this.destinationLockedToRequest.set(true);
      }
    }

    // The provider's accounts, so the destination field can render the one this payment names. Loaded only
    // when a person picks a provider until now, which left every OPENED payment with an empty picker: the
    // id was patched in and matched no option, so a payment with a destination displayed as having none.
    // Never auto-selects here — see setProviderBanks.
    if (data.providerReference) {
      this.applyProviderBanks({ id: data.providerReference } as Business, false);
    }

    // A saved allocation snapshots only the document number + amount, not the due date — pull it from the
    // provider's payable documents and patch it onto each row (#4349). Flat path only: the merged draft
    // rows carry their due dates from the payable data itself (and are not even built yet here).
    if (!editableDraft && data.providerReference) this.patchAllocationDueDates(data.providerReference, data.currency);
  }

  /** One flat row from a saved allocation — the snapshot view: exactly what the payment holds, with the
   * settlement standing in as its own cap. Every allocation of a posted or request-owned payment renders
   * this way; on a reloaded draft, only a document the payable list no longer offers.
   *
   * <p>The discharge is stored now (#5144), so it is read rather than reconstructed — reconstructing it
   * from cash would be wrong the moment the allocation converted.</p> */
  private flatAllocationRow(alloc: ApPaymentAllocation, paymentCurrency: Currency): FormGroup<ApPaymentAllocationFormGroup> {
    const cash = alloc.allocatedAmount ?? 0;
    const advance = alloc.advanceAppliedAmount ?? 0;
    const settlement = alloc.settledAmount ?? Math.round((cash + advance) * 100) / 100;
    return this.formBuilder.group<ApPaymentAllocationFormGroup>({
      documentReference: new FormControl<string>(alloc.documentReference, { nonNullable: true }),
      documentNumber: new FormControl<string>(alloc.documentNumber, { nonNullable: true }),
      dueDate: new FormControl<string | null>(null),
      netPayable: new FormControl<number>(settlement, { nonNullable: true }),
      selected: new FormControl<boolean>(true, { nonNullable: true }),
      // A saved allocation is what the payment already holds — never re-gated here. The post re-checks
      // it server-side, which is the authority; flagging it in this list would only be noise.
      withholdingBlocked: new FormControl<boolean>(false, { nonNullable: true }),
      allocatedAmount: new FormControl<number>(settlement, { nonNullable: true }),
      isCredit: new FormControl<boolean>(settlement < 0, { nonNullable: true }),
      documentCurrency: new FormControl<Currency>(alloc.documentCurrency ?? paymentCurrency, { nonNullable: true }),
      // The invoice's BOOKING rate (#5135), not the doc → payment one beside it: this control is the
      // fallback numerator when the payment has no quote for that currency, and the two are only equal
      // when the payment happens to be in the functional currency.
      documentExchangeRate: new FormControl<number>(alloc.documentExchangeRate ?? alloc.exchangeRate ?? 1, { nonNullable: true }),
      docWithholding: new FormControl<number>(alloc.withholdingAmount ?? 0, { nonNullable: true }),
      advanceToUse: new FormControl<number>(advance, { nonNullable: true }),
      advanceAvailable: new FormControl<number>(0, { nonNullable: true }),
      // Restored from the saved tag — auto-filled server-side when the payment was spawned from a
      // request that sat in exactly one cash plan.
      cashRequestReference: new FormControl<string | null>(alloc.cashRequest?.reference ?? null),
      fiscalNumber: new FormControl<string>(alloc.fiscalNumber ?? '', { nonNullable: true }),
    });
  }

  /** Fill in the invoice due dates on a loaded/spawned payment's rows from the payable document set (the
   * allocation itself doesn't carry them). A posted invoice that is already fully paid won't be in the set, so
   * its row keeps a blank due date. */
  private patchAllocationDueDates(providerReference: string, currency: Currency): void {
    this.documentService.getPayableByProvider(providerReference, currency).subscribe({
      next: (response) => {
        const dueByDoc = new Map<string, string | null>();
        for (const doc of response.data ?? []) if (doc.id) dueByDoc.set(doc.id, doc.dueDate ?? null);
        for (const row of this.allocations.controls) {
          const due = dueByDoc.get(row.controls.documentReference.value);
          if (due) row.controls.dueDate.setValue(due, { emitEvent: false });
        }
      },
      error: () => undefined,
    });
  }

  // ---- save / post ----
  private buildInsert(): ApPaymentInsert {
    const v = this.form.getRawValue();
    return {
      providerReference: v.provider?.id ?? '',
      financialCompanyReference: '', // API resolves from the current site
      paymentDate: (v.paymentDate ?? new Date()).toISOString(),
      amount: this.amount(),
      transferFee: v.bankingFee ?? 0,
      securityTax: v.securityFee ?? 0,
      currency: v.currency,
      bankAccountReference: v.bankAccount ?? undefined,
      selectedProviderBankId: v.selectedProviderBankId ?? null,
      notes: v.notes ?? null,
      settlementRates: this.buildSettlementRates(),
      allocations: this.buildAllocations(),
    };
  }

  /** Expand each selected group's settlement into per-document allocations doc-first (#4516), splitting the base
   * document's portion into settlement + advance (#4349, #5144): settledAmount = the whole discharge in the
   * document's currency, advanceAppliedAmount = the part of it covered by an advance. The API derives the
   * cash. Payload stays one allocation per document. */
  private buildAllocations(): ApPaymentAllocationInsert[] {
    const out: ApPaymentAllocationInsert[] = [];
    for (const row of this.allocations.controls) {
      if (!row.controls.selected.value) continue;
      const settlement = row.controls.allocatedAmount.value ?? 0;
      if (settlement === 0) continue;
      const payable = this.payableByDoc.get(row.controls.documentReference.value);
      const advance = Math.min(row.controls.advanceToUse.value ?? 0, Math.max(settlement, 0));
      // The tag belongs to the group the user set it on, but the payload is per document — stamp it on every
      // member so it survives the doc-first split, the same way the request's note does.
      const cashRequestReference = row.controls.cashRequestReference.value || null;
      if (payable && isPayableBaseGroup(payable)) {
        for (const entry of distributePayable(payable, settlement)) {
          const isBase = entry.apDocumentReference === (payable.document.id ?? '');
          const adv = isBase ? Math.min(advance, entry.amount) : 0;
          out.push({
            documentReference: entry.apDocumentReference,
            // The whole discharge, advance included (#5144) — the API subtracts the advance and converts.
            settledAmount: entry.amount,
            advanceAppliedAmount: adv,
            cashRequestReference,
          });
        }
      } else {
        // Flat (loaded) row or standalone note: settlement − advance is cash; advance is 0 for a note.
        out.push({
          documentReference: row.controls.documentReference.value,
          settledAmount: settlement,
          advanceAppliedAmount: advance,
          cashRequestReference,
        });
      }
    }
    return out;
  }

  private validateForPost(): boolean {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    // Must settle a positive net — cash or advance (a fully-advance-covered payment has 0 cash) (#4349).
    if (this.amount() + this.totalAdvance() <= 0) {
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    // Each group's amount must be within its net payable (credit rows are negative → compare on magnitude).
    const over = this.allocations.controls.some(
      (r) =>
        r.controls.selected.value && Math.abs(r.controls.allocatedAmount.value ?? 0) > Math.abs(r.controls.netPayable.value ?? 0) + 0.01,
    );
    if (over) return false;
    return true;
  }

  /** Draft save is lighter than post: provider + at least one valid allocation; the bank account is
   * only required at post time (an auto-created draft starts with none). */
  private validateForDraft(): boolean {
    if (!this.form.controls.provider.value) {
      this.form.controls.provider.markAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    if (this.amount() + this.totalAdvance() <= 0) return false;
    const over = this.allocations.controls.some(
      (r) =>
        r.controls.selected.value && Math.abs(r.controls.allocatedAmount.value ?? 0) > Math.abs(r.controls.netPayable.value ?? 0) + 0.01,
    );
    return !over;
  }

  saveDraft(): void {
    if (!this.validateForDraft()) return;
    this.startLoading();
    const id = this.currentId();
    const obs = !this.isNew() && id ? this.service.updateDraft(id, this.buildInsert()) : this.service.createDraft(this.buildInsert());
    obs.pipe(finalize(() => this.stopLoading())).subscribe({
      next: (response) => {
        const saved = response.data;
        if (saved?.id) {
          this.isNew.set(false);
          this.currentId.set(saved.id);
          this.status.set(saved.status);
          this.paymentNumber.set(saved.number ?? '');
          // What the payment now STORES, not what the form sent — the server defaults, prunes and derives
          // the rate table on save (#5135), and the panel has to show the stored truth. Same for the
          // destination snapshot the save just froze.
          const savedBankCurrency = saved.banking?.paymentBank?.currency;
          this.savedDestination.set(
            saved.selectedProviderBankId && savedBankCurrency != null
              ? { id: saved.selectedProviderBankId, currency: savedBankCurrency as Currency }
              : null,
          );
          this.applySettlementRates(saved.settlementRates ?? []);
          // The allocations are this provider's documents; a saved payment changing provider would
          // invalidate every one of them, so the field locks the moment the payment exists.
          this.form.controls.provider.disable({ emitEvent: false });
          this.router.navigate([], { queryParams: { id: saved.id }, replaceUrl: true });
        }
      },
      error: (error) => console.error('Error saving AP payment draft:', error),
    });
  }

  openRequest(): void {
    const id = this.requestId();
    if (id) this.router.navigate(['/ap-payment-request-details'], { queryParams: { id } });
  }

  post(): void {
    if (!this.validateForPost()) return;
    this.confirmationService.confirm({
      header: this.t('ApPayment_Post_Confirm_Header', 'Post Payment'),
      message: this.t(
        'ApPayment_Post_Confirm_Message',
        'Posting saves your changes, creates the journal entry, moves the bank balance, and settles the allocated invoices. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.doPost(),
    });
  }

  private doPost(): void {
    const bankTransactionNumber = this.form.controls.bankTransactionReference.value ?? null;
    const existingId = this.currentId();
    this.startLoading();

    // Save first, post second — what you see is what posts (same rule as ap-document-details). The screen
    // can hold edits the stored draft does not — a changed bank rate, a re-allocated invoice — and posting
    // reads the STORED payment. The new-payment path always saved first; the existing-draft path posted
    // the stored version and silently dropped whatever was on screen.
    //
    // If the save fails, the post never runs. If the save lands and the post then refuses, the edits are
    // at least saved — reopening the draft shows exactly what the refusal was about.
    const save =
      !this.isNew() && existingId ? this.service.updateDraft(existingId, this.buildInsert()) : this.service.createDraft(this.buildInsert());

    save
      .pipe(
        switchMap((response) => {
          const saved = response.data;
          if (!saved?.id) throw new Error('The draft did not save, so nothing was posted.');
          this.currentId.set(saved.id);
          this.isNew.set(false);
          return this.service.post(saved.id, { bankTransactionNumber });
        }),
        finalize(() => this.stopLoading()),
      )
      .subscribe({
        next: () => this.router.navigate(['/ap-payments']),
        error: (error) => console.error('Error posting AP payment:', error),
      });
  }

  // ---- receipt (file comes from the shared bank-charges section) ----
  onReceiptFile(file: File): void {
    const id = this.currentId();
    if (!file || !id) return;
    this.startLoading();
    this.service
      .uploadReceipt(id, file)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.attachments.set(response.data.attachments ?? []);
        },
        error: (error) => console.error('Error uploading receipt:', error),
      });
  }

  openAttachment(url: string | null | undefined): void {
    if (url) window.open(url, '_blank');
  }

  back(): void {
    this.router.navigate(['/ap-payments']);
  }

  getRowGroup(row: unknown): FormGroup<ApPaymentAllocationFormGroup> {
    return row as FormGroup<ApPaymentAllocationFormGroup>;
  }

  /** Display label for an attachment: the file name only, stripping the durable S3 key path. */
  attachmentFileName(name: string | null | undefined): string {
    if (!name) return '';
    const segments = name.split('/');
    return segments[segments.length - 1] || name;
  }

  /**
   * Deletes this draft, after confirming (#5223).
   *
   * <p>Offered only on a SAVED draft: there is nothing to delete before the first save. The API applies the
   * same draft-only rule, so a record that moved on in another tab is answered by that refusal rather than
   * by whatever this screen last painted — the button is a convenience, not the guard.</p>
   *
   * <p>Hard delete: a draft has posted nothing, so there is no ledger entry to keep and nothing a surviving
   * row would explain. Hence the explicit warning that it cannot be undone.</p>
   */
  /**
   * Back to draft — for a payment posted with something wrong in it.
   *
   * <p>Confirmed rather than immediate, because it moves real money back: the ledger entry is reversed, the
   * invoices stop reading Paid, the advances get their capacity back and the bank movement is voided. None
   * of that is hidden from the user, since the whole reason to do it is that one of those figures is
   * wrong.</p>
   */
  confirmUnpost(): void {
    const id = this.currentId();
    if (!id || !this.isPosted() || !this.canUnpost) return;
    this.confirmationService.confirm({
      header: this.t('ApPayment_Unpost_Title', 'Return payment to draft'),
      message: this.t(
        'ApPayment_Unpost_Confirm',
        'This reverses the journal entry, puts the invoices back to what they owed, returns any advance ' +
          'that was drawn down, and voids the bank movement. The payment becomes editable and can be ' +
          'posted again. Continue?',
      ),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('ApPayment_Unpost_Accept', 'Return to draft'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.unpost(id),
    });
  }

  private unpost(id: string): void {
    this.startLoading();
    this.service
      .unpost(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: () => {
          GlobalMessageUtil.success(
            this.t('ApPayment_Unpost_Done_Title', 'Back to draft'),
            this.t('ApPayment_Unpost_Done', 'The payment was reversed and is editable again.'),
          );
          // Reloaded rather than patched from the response: the payment comes back Draft, which changes
          // which sections render and which controls are enabled, and the invoices it settled now owe
          // money again. Re-reading is the one way the whole screen agrees about that.
          this.loadPayment(id);
        },
        error: (error) => console.error('Error returning AP payment to draft:', error),
      });
  }

  confirmDelete(): void {
    const id = this.currentId();
    if (!id || !this.isDraft()) return;
    this.confirmationService.confirm({
      header: this.t('ApPayment_Delete_Title', 'Delete draft'),
      message: this.t('ApPayment_Delete_Confirm', 'Delete this draft payment? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Delete', 'Delete'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteDraft(id),
    });
  }

  private deleteDraft(id: string): void {
    this.service.delete(id).subscribe({
      next: () => {
        GlobalMessageUtil.success(
          this.t('ApPayment_Delete_Done_Title', 'Draft deleted'),
          this.t('ApPayment_Delete_Done', 'The draft was deleted.'),
        );
        this.router.navigate(['/ap-payments']);
      },
    });
  }
}
